/**
 * useLivePortfolio
 *
 * React hook that polls /api/portfolio/summary/live or /api/portfolio/live
 * every 15 seconds and provides live-priced data to the caller.
 *
 * Design:
 *   - Starts polling immediately on mount.
 *   - Values are converted via NBP FX rates from the DB.
 *   - Cleans up the interval on unmount.
 *   - Does not throw — errors are silently ignored; last known data is kept.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useApiClient } from '../api/client';
import type { PortfolioLiveResponse, PortfolioSummaryLive } from '../api/client';

export const LIVE_POLL_INTERVAL_MS = 15_000;

// ── Summary variant ────────────────────────────────────────────────────────────

export interface UseLiveSummaryResult {
  summary: PortfolioSummaryLive | null;
  loading: boolean;
  lastUpdated: Date | null;
}

export function useLiveSummary(): UseLiveSummaryResult {
  const api = useApiClient();
  const [summary, setSummary] = useState<PortfolioSummaryLive | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const isMounted = useRef(true);

  const fetchSummary = useCallback(async () => {
    try {
      const data = await api.getPortfolioSummaryLive();
      if (isMounted.current) {
        setSummary(data);
        setLastUpdated(new Date());
      }
    } catch {
      // Fail silently — keep last known data
    } finally {
      if (isMounted.current) setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    isMounted.current = true;
    void fetchSummary();
    const id = setInterval(() => {
      void fetchSummary();
    }, LIVE_POLL_INTERVAL_MS);
    return () => {
      isMounted.current = false;
      clearInterval(id);
    };
  }, [fetchSummary]);

  return { summary, loading, lastUpdated };
}

// ── Positions variant ──────────────────────────────────────────────────────────

export interface UseLivePortfolioResult {
  liveData: PortfolioLiveResponse | null;
  loading: boolean;
  lastUpdated: Date | null;
}

export function useLivePortfolio(): UseLivePortfolioResult {
  const api = useApiClient();
  const [liveData, setLiveData] = useState<PortfolioLiveResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const isMounted = useRef(true);

  const fetchLive = useCallback(async () => {
    try {
      const data = await api.getPortfolioLive();
      if (isMounted.current) {
        setLiveData(data);
        setLastUpdated(new Date());
      }
    } catch {
      // Fail silently — keep last known data
    } finally {
      if (isMounted.current) setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    isMounted.current = true;
    void fetchLive();
    const id = setInterval(() => {
      void fetchLive();
    }, LIVE_POLL_INTERVAL_MS);
    return () => {
      isMounted.current = false;
      clearInterval(id);
    };
  }, [fetchLive]);

  return { liveData, loading, lastUpdated };
}
